// components/artifacts/renderers/SqlArtifactRenderer.tsx
import React, { useMemo } from "react";
import { Copy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import CodeBlockCard from "@/components/cards/message/CodeBlockCard";
import { TableArtifactRenderer } from "./TableArtifactRenderer";
import { SqlArtifact, SqlQueryResult, TableData } from "@/types/Artifact";
import { copyToClipboard, downloadArtifact } from "@/lib/artifact-utils";

interface SqlArtifactRendererProps {
  artifact: SqlArtifact;
}

export const SqlArtifactRenderer: React.FC<SqlArtifactRendererProps> = React.memo(({ 
  artifact 
}) => {
  const { query, result } = useMemo(() => {
    if (typeof artifact.content === "string") {
      return { query: artifact.content, result: null };
    }
    
    const sqlContent = artifact.content as SqlQueryResult;
    return {
      query: sqlContent.query || "",
      result: sqlContent.result || null,
    };
  }, [artifact.content]);

  const handleCopyQuery = () => copyToClipboard(query);
  const handleDownload = () => downloadArtifact(artifact);

  const renderResult = () => {
    if (!result) return null;

    // If result is a string, try to parse it
    if (typeof result === "string") {
      try {
        const parsed = JSON.parse(result) as TableData;
        return (
          <TableArtifactRenderer 
            artifact={{ 
              ...artifact, 
              type: "table", 
              content: parsed 
            }} 
          />
        );
      } catch {
        // If parsing fails, show as plain text
        return (
          <div className="p-4">
            <pre className="whitespace-pre-wrap text-sm">{result}</pre>
          </div>
        );
      }
    }

    // Result is already TableData
    return (
      <TableArtifactRenderer 
        artifact={{ 
          ...artifact, 
          type: "table", 
          content: result 
        }} 
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* SQL Query */}
      <div className="relative">
        <div className="absolute right-2 top-2 flex space-x-2 z-10">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleCopyQuery}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy SQL</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleDownload}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download SQL</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        <CodeBlockCard 
          language="sql" 
          code={query} 
        />
      </div>

      {/* SQL Results */}
      {result && (
        <>
          <Separator />
          <div>
            <h3 className="mb-2 px-4 text-sm font-medium text-muted-foreground">
              Query Results
            </h3>
            {renderResult()}
          </div>
        </>
      )}
    </div>
  );
});